import PropTypes from 'prop-types';
import React from 'react';
import {
  isString as _isString,
  isNil as _isNil,
} from 'lodash';

import isTouch from 'utils/isTouch';
import mimeLookup from 'utils/mimeLookup';
import { optionalAttribute } from 'utils/jsx';
import { src } from 'utils/imgix';
import { videoConfigProps } from 'utils/propTypes';

import VideoPlayButton from 'components/VideoPlayButton/VideoPlayButton';

class InlineVideo extends React.Component {
  constructor(props) {
    super(props);
    this.handleVideoPlay = this.handleVideoPlay.bind(this);
    this.state = {
      videoPlaying: false,
    };
  }

  componentDidMount() {
    // Explicitly check that property exists as false. Simply `null` should default to autoplay = true
    if (this.props.videoConfig.autoPlay === false) {
      return;
    }

    this.handleVideoPlay();
  }

  handleVideoPlay() {
    this.setState({
      videoPlaying: true,
    });
    this.video.play();
  }

  renderFallbackImage() {
    if (!this.props.videoConfig.fallback) {
      return null;
    }

    const imageClass = optionalAttribute(`className`, this.props.className, this.props.className && isTouch());
    const fallbackWidth = this.props.videoConfig.posterWidth ? this.props.videoConfig.posterWidth : 1200;

    return (
      <img
        alt={this.props.imageDescription}
        src={src(this.props.videoConfig.fallback, fallbackWidth)}
        {...imageClass}
      />
    );
  }

  renderSources() {
    if (_isString(this.props.videoConfig.path)) {
      return (
        <source
          src={`https://d270gkllvn2eew.cloudfront.net/${this.props.videoConfig.path}`}
          type={mimeLookup(this.props.videoConfig.path)}
        />
      );
    }

    return this.props.videoConfig.path.map((sourcePath, i) => {
      return (
        <source
          key={i}
          src={`https://d270gkllvn2eew.cloudfront.net/${sourcePath}`}
          type={mimeLookup(sourcePath)}
        />
      );
    });
  }

  renderVideo() {
    const videoClass = optionalAttribute(`className`, this.props.className, this.props.className);
    const height = optionalAttribute(`height`, this.props.videoConfig.height, this.props.videoConfig.height);
    const width = optionalAttribute(`width`, this.props.videoConfig.width, this.props.videoConfig.width);
    const posterWidth = this.props.videoConfig.posterWidth ? this.props.videoConfig.posterWidth : 1200;
    const posterUrl = this.props.videoConfig.poster ? src(this.props.videoConfig.poster, posterWidth) : null;
    const poster = optionalAttribute(`poster`, posterUrl, this.props.videoConfig.poster);
    const preload = optionalAttribute(`preload`, this.props.videoConfig.preload, this.props.videoConfig.preload);
    const canplaythrough = optionalAttribute(`onCanPlayThrough`, this.props.canplaythrough, this.props.canplaythrough);

    return (
      <video
        autoPlay={_isNil(this.props.videoConfig.autoPlay) ? true : this.props.videoConfig.autoPlay}
        loop={_isNil(this.props.videoConfig.loop) ? true : this.props.videoConfig.loop}
        muted="true"
        playsInline="true"
        ref={(el) => { this.video = el; }}
        {...canplaythrough}
        {...height}
        {...poster}
        {...preload}
        {...videoClass}
        {...width}
      >
        {this.renderSources()}
        {this.renderFallbackImage()}
      </video>
    );
  }

  renderPlayButton() {
    if (this.state.videoPlaying) {
      return null;
    }

    return (
      <VideoPlayButton
        color={this.props.playButtonColor}
        handleVideoPlay={this.handleVideoPlay}
      />
    );
  }

  render() {
    if (!this.props.includePlayButton) {
      return this.renderVideo();
    }

    return (
      <div>
        {this.renderPlayButton()}
        {this.renderVideo()}
      </div>
    );
  }
}

InlineVideo.propTypes = {
  canplaythrough: PropTypes.func,
  className: PropTypes.string,
  imageDescription: PropTypes.string,
  includePlayButton: PropTypes.bool,
  playButtonColor: PropTypes.oneOf([
    `blue`,
    `gray`,
  ]),
  preventFallback: PropTypes.bool,
  videoConfig: videoConfigProps,
};

export default InlineVideo;
